import { useStyletron } from "baseui";
import { Block } from "baseui/block";
import { Button, ButtonProps, KIND, SHAPE, SIZE } from "baseui/button";
import { Check } from "baseui/icon";
import { ListItem, ListItemLabel } from "baseui/list";
import { Modal, ModalBody, ModalButton, ModalFooter, ModalHeader, ROLE } from "baseui/modal";
import { useSnackbar } from "baseui/snackbar";
import { ParagraphXSmall } from "baseui/typography";
import { useState, useEffect } from "react";
import { VscCombine, VscDebugLineByLine, VscListSelection } from "react-icons/vsc";
import { IAutomationObject } from "../../../automationObjects";
import { ObjectNavigatorModal } from "../../ObjectSelector/Modal";
import { ObjectTemplatesView } from "./ObjectTemplatesView";
import { Notification } from "baseui/notification";
import { ProgressSteps, Step } from "baseui/progress-steps";
import { AccessRoles, useHasRole } from "../../../hooks/useHasRole";
import { ObjectTemplate, RunObjectTemplateCommand, useRunObjectTemplateMutation } from "../../../services/objectsTemplates";
import { ButtonGroup, MODE } from "baseui/button-group";
import { FormControl } from "baseui/form-control";
import { Select } from "baseui/select";
import { getUserId } from "../../../services/silentAuth";
import { useGetLanguage } from "../../../hooks/useGetLanguage";
import { TCompany } from "../../../automationObjects/Agents/Organizations/Company/types";
import { useGetUsersResourceGroupsQuery } from "../../../services/resourceGroups/userApi";



export const ObjectTemplatesModal = () => {
  const { language } = useGetLanguage();
  const hasAccessObjectWrite = useHasRole(AccessRoles.Object.Write);
  const { enqueue } = useSnackbar();
  const [css, theme] = useStyletron();
  const [current, setCurrent] = useState(0);

  const [runTemplate, runTemplateResult] = useRunObjectTemplateMutation();

  const [isOpen, setIsOpen] = useState(false);
  const [showObjectSelector, setShowObjectSelector] = useState(false);
  function onShowObjectSelector() {
    setShowObjectSelector(true);
  }

  const [selectedParent, setSeletctedParent] = useState<IAutomationObject>();
  const onParentSelected = (item: IAutomationObject) => setSeletctedParent(item);

  const [selectedTemplate, setSeletctedTemplate] = useState<ObjectTemplate>();
  const [appType, setAppType] = useState<number>(0);
  const userId = getUserId();
  const { data: allowedResourceGroups, isSuccess } = useGetUsersResourceGroupsQuery(userId || '', { skip: !userId});
  const [resourceGroup, setResourceGroup] = useState<any>();
  const resourceGroupsSelect = allowedResourceGroups?.map((item: TCompany, index: number) => { return { label: item.name, id: item.id } });
  const onResourceGroupChanged = (e: any) => {
      setResourceGroup(e.value[0]);
  }

  useEffect(() => {
    if (isSuccess) {
        const rgfound = allowedResourceGroups?.filter((item, index) => item.id === selectedParent?.resourceGroupId)[0];
        setResourceGroup({ label: rgfound?.name, id: rgfound?.id });
    }
}, [isSuccess]);

  function onRunTemplate() {
    const cmd: RunObjectTemplateCommand = {
      template: selectedTemplate?.name || '',
      parentId: appType === 0 ? selectedParent?.id || '' : '00000000-0000-0000-0000-000000000000',
      resourceGroupId: appType === 0 ? selectedParent?.resourceGroupId || '' : resourceGroup?.id,
      language:  language.startsWith("de") ? 'de' : language.startsWith("es") ? 'es' : 'en',
      isRoot: appType === 0 ? false : true
    }
    runTemplate(cmd);
  }

  useEffect(() => {
    if (runTemplateResult?.isSuccess) {
      enqueue({
        message: 'Template sucessfully executed',
        startEnhancer: ({ size }) => <Check size={size} />,
      });
      setIsOpen(false);
      setCurrent(0);
    }
  }, [runTemplateResult]);

  const canRun = (selectedParent && selectedTemplate && appType === 0) || (resourceGroup?.id && selectedTemplate && appType === 1)
  const canOpen = hasAccessObjectWrite;

  const buttonConditions = [
    {
      next: { disabled: !selectedTemplate, onClick: () => setCurrent(1) }
    },
    {
      next: { disabled: false, onClick: () => setCurrent(2) },
      prev: { onClick: () => setCurrent(0) }
    },
    {
      next: { disabled: (!selectedParent && appType === 0) || (!resourceGroup?.id && appType === 1) , onClick: () => setCurrent(3) },
      prev: { onClick: () => setCurrent(2) }
    }
  ]
  const nextButtonProps = buttonConditions[current]?.next;
  const prevButtonProps = buttonConditions[current]?.prev;


  return (
    <>
      <Button size="compact" disabled={!canOpen} onClick={() => setIsOpen(true)} kind='secondary' startEnhancer={() => <VscDebugLineByLine size={14} />}>
        Templates
      </Button>
      <Modal overrides={{
        Dialog: {
          style: {
            width: '80vw',
            height: '90vh',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'space-between'
          },
        },
      }}
        closeable onClose={() => setIsOpen(false)} isOpen={isOpen}>
        <ModalHeader>Run Object Template</ModalHeader>
        <ModalBody style={{ flex: '1 1 0' }}>
          {runTemplateResult.error &&
            <Notification kind='negative'>
              There has been an error: {JSON.stringify(runTemplateResult.error)}
            </Notification>
          }

          <Block width='100%' display='flex' flexDirection='column' >
            <ProgressSteps current={current}>
              <Step title={"Select a Template you want to run"}>

                <ObjectTemplatesView onTemplateSelected={setSeletctedTemplate}></ObjectTemplatesView>
              </Step>
              <Step title={"Select if you want to create a root App or select a parent App"}>
                <ButtonGroup
                  mode={MODE.radio}
                  selected={appType}
                  onClick={(_event, index) => {
                    setAppType(index);
                  }}
                  size={SIZE.large}
                  kind="secondary"
                >
                  <Button startEnhancer={() => <VscCombine></VscCombine>}>
                    Select a parent App
                  </Button>
                  <Button startEnhancer={() => <VscListSelection></VscListSelection>}>Create a root App</Button>
                </ButtonGroup>
              </Step>
              <Step title="Select an App as Parent or a Resourcegroup">
                {appType === 0 && <>
                  <ParagraphXSmall>Please select a parent App</ParagraphXSmall>
                  <Button onClick={onShowObjectSelector}>Select Parent</Button>
                  {selectedParent &&
                    <ListItem>
                      <ListItemLabel description={selectedParent.description}>
                        {selectedParent.name}
                      </ListItemLabel>
                    </ListItem>
                  }
                  {!selectedParent &&
                    <Notification kind="warning" >Please select a parent App</Notification>
                  }
                  {showObjectSelector &&
                    <ObjectNavigatorModal title='Select App as parent'
                      isOpen={showObjectSelector}
                      showAddObject
                      setIsOpen={(e: boolean) => setShowObjectSelector(e)}
                      onSelected={onParentSelected}
                      currentSelectedParent={selectedParent}></ObjectNavigatorModal>
                  }
                </>
                }
                {appType === 1 &&
                  <>
                    <ParagraphXSmall>Select a resourcegroup for the App</ParagraphXSmall>
                    <FormControl
                                label={() => "Resource Group"}
                                caption={() => "Select the resource Group"}
                            >
                                <Select
                                    placeholder=''
                                    options={resourceGroupsSelect}
                                    value={resourceGroup}
                                    onChange={onResourceGroupChanged} 
                                />
                            </FormControl>
                  </>
                }
              </Step>
            </ProgressSteps>
          </Block>
        </ModalBody>
        <ModalFooter>
          {prevButtonProps && <ModalButton kind="tertiary" {...prevButtonProps}>
            Back
          </ModalButton>}
          {nextButtonProps && <ModalButton kind="secondary" {...nextButtonProps}>
            Next
          </ModalButton>}
          {current === 3 &&
            <>
              <ModalButton kind='tertiary' onClick={() => setIsOpen(false)}>
                Cancel
              </ModalButton>
              <ModalButton isLoading={runTemplateResult.isLoading} disabled={!canRun} onClick={onRunTemplate}>Run</ModalButton>
            </>
          }
        </ModalFooter>
      </Modal>
    </>
  )
}